import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STEPS = [
  {
    tab: 'home',
    icon: '🏠',
    title: 'Home — your recent form',
    body: "Your last 30 days at a glance: the performance trend behind every match, recent results, and development areas Coach has picked out of your own notes.",
    highlight: 'Home',
    scrollTo: null,
  },
  {
    tab: 'session_reports',
    icon: '📊',
    title: 'Game analytics — the full history',
    body: "Averages, results and matches in one place: dashboard, head-to-head records, competitions, stats by equipment, notes library and analytics.",
    highlight: 'Game analytics',
    scrollTo: null,
  },
  {
    tab: 'program',
    icon: '🎯',
    title: 'Game Plans — plan and log',
    body: "Plan each session in advance: set sport, goal, intensity and tactical focus. After training, open the Game Plan to log the result, rate your performance and add notes.",
    highlight: 'Game Plans',
    scrollTo: null,
  },
  {
    tab: 'chat',
    icon: '💬',
    title: 'Coach — your AI coach',
    body: "Coach reads your plans, results and notes. Ask things like 'What went wrong in my last match?' or 'How has my serve been trending?'",
    highlight: 'Coach',
    scrollTo: null,
  },
];

const S = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9000,
    pointerEvents: 'none',
  },
  panel: {
    position: 'fixed',
    bottom: '80px',
    right: '20px',
    width: '320px',
    background: 'rgba(6, 14, 28, 0.97)',
    border: '1px solid rgba(0, 212, 255, 0.30)',
    borderRadius: '16px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,212,255,0.08)',
    padding: '16px 20px',
    pointerEvents: 'all',
    fontFamily: "'Inter', -apple-system, sans-serif",
    backdropFilter: 'blur(12px)',
    animation: 'tutorialFadeIn 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
  },
  stepBadge: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'rgba(0,212,255,0.7)',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  icon: {
    fontSize: '22px',
    marginBottom: '6px',
  },
  title: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#ffffff',
    margin: '0 0 8px',
    lineHeight: 1.3,
  },
  body: {
    fontSize: '13px',
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.72)',
    margin: '0 0 4px',
    whiteSpace: 'pre-line',
  },
  dots: {
    display: 'flex',
    gap: '5px',
    marginBottom: '12px',
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  dot: (active) => ({
    width: active ? '16px' : '5px',
    height: '5px',
    borderRadius: '3px',
    background: active ? '#00d4ff' : 'rgba(255,255,255,0.18)',
    transition: 'all 0.2s ease',
  }),
  buttons: {
    display: 'flex',
    gap: '8px',
    flexShrink: 0,
  },
  btnNext: {
    flex: 1,
    background: '#00d4ff',
    color: '#060e1c',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 0',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
  },
  btnBack: {
    background: 'transparent',
    color: 'rgba(255,255,255,0.50)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    padding: '9px 14px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
  },
  btnSkip: {
    background: 'transparent',
    color: 'rgba(255,255,255,0.30)',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 10px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
  },
};

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 600);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mobile;
}

function scrollToSection(id) {
  if (!id) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
  setTimeout(() => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 80);
}

export default function TutorialOverlay({ onTabChange, activeTab, onDone, onOpenSession, onCloseSession, isDemoMode }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const isMobile = useIsMobile();

  const dismiss = useCallback(() => {
    setVisible(false);
    localStorage.setItem('tutorial_done', '1');
    onDone?.();
  }, [onDone]);

  const next = useCallback(() => {
    if (step < STEPS.length - 1) {
      const nextStep = step + 1;
      setStep(nextStep);
      const cur = STEPS[step];
      const nxt = STEPS[nextStep];
      if (nxt.tab !== cur.tab) onTabChange?.(nxt.tab);
      scrollToSection(nxt.scrollTo);
      if (nxt.openSession) {
        onOpenSession?.(nxt.openSession.date, nxt.openSession.sessionTab);
      } else if (cur.openSession) {
        onCloseSession?.();
      }
    } else {
      dismiss();
    }
  }, [step, dismiss, onTabChange, onOpenSession, onCloseSession]);

  const back = useCallback(() => {
    if (step > 0) {
      const prevStep = step - 1;
      setStep(prevStep);
      const cur = STEPS[step];
      const prv = STEPS[prevStep];
      if (prv.tab !== cur.tab) onTabChange?.(prv.tab);
      scrollToSection(prv.scrollTo);
      if (prv.openSession) {
        onOpenSession?.(prv.openSession.date, prv.openSession.sessionTab);
      } else if (cur.openSession) {
        onCloseSession?.();
      }
    }
  }, [step, onTabChange, onOpenSession, onCloseSession]);

  // Sync step when user navigates manually via bottom nav
  useEffect(() => {
    if (!visible || !activeTab) return;
    // Only sync to first step of that tab to avoid overriding home sub-steps
    const idx = STEPS.findIndex(s => s.tab === activeTab);
    if (idx !== -1 && STEPS[step]?.tab !== activeTab) setStep(idx);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (localStorage.getItem('tutorial_done') === '1') return;

    const urlParams = new URLSearchParams(window.location.search);
    const tourParam = urlParams.get('tour');

    if (tourParam === 'true' || isDemoMode) {
      setVisible(true);
      onTabChange?.(STEPS[0].tab);
      scrollToSection(STEPS[0].scrollTo);
      if (tourParam === 'true') window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    fetch(`${API}/tutorial/check`)
      .then(r => r.json())
      .then(({ show }) => {
        if (show) {
          setVisible(true);
          onTabChange?.(STEPS[0].tab);
        }
      })
      .catch(e => console.error('[TutorialOverlay] show-check failed:', e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  const panelStyle = isMobile ? {
    ...S.panel,
    bottom: '70px',
    right: '10px',
    left: '10px',
    width: 'auto',
    padding: minimized ? '10px 16px' : '12px 16px',
    borderRadius: '14px',
  } : {
    ...S.panel,
    maxHeight: minimized ? 'auto' : '60vh',
  };

  return (
    <>
      <style>{`
        @keyframes tutorialFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={S.overlay}>
        <div style={panelStyle}>

          {/* Header row */}
          <div style={{ ...S.stepBadge, marginBottom: minimized ? 0 : 10 }}>
            <span>Step {step + 1} of {STEPS.length} — {s.highlight}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setMinimized(m => !m)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
                title={minimized ? 'Expand' : 'Minimise'}
              >
                {minimized ? '▲' : '▼'}
              </button>
              <button
                onClick={dismiss}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: 14, lineHeight: 1, padding: '0 2px' }}
                title="Close tutorial"
              >✕</button>
            </div>
          </div>

          {!minimized && (
            <>
              <div style={{ overflowY: 'auto', flex: 1, marginBottom: 12, paddingRight: 2 }}>
                {!isMobile && <div style={S.icon}>{s.icon}</div>}
                <h3 style={{ ...S.title, fontSize: isMobile ? '13px' : '15px' }}>{s.title}</h3>
                <p style={{ ...S.body, fontSize: isMobile ? '12px' : '13px' }}>{s.body}</p>
              </div>

              <div style={S.dots}>
                {STEPS.map((_, i) => (
                  <div key={i} style={S.dot(i === step)} />
                ))}
              </div>

              <div style={S.buttons}>
                {!isFirst ? (
                  <button style={S.btnBack} onClick={back}>← Back</button>
                ) : (
                  <button style={S.btnSkip} onClick={dismiss}>✕ Close</button>
                )}
                <button style={S.btnNext} onClick={next}>
                  {isLast ? 'Get started' : 'Next →'}
                </button>
              </div>
            </>
          )}

          {minimized && (
            <button style={{ ...S.btnNext, marginTop: 8 }} onClick={() => setMinimized(false)}>
              {s.title} — tap to expand
            </button>
          )}

        </div>
      </div>
    </>
  );
}
